import { useState } from "react";
import { Copy, Link2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, SectionTitle } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { t } from "@/i18n";

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(t("p7.share.copied"));
  } catch {
    toast.error(t("p7.share.copyFailed"));
  }
};

export function ShareLinkCard() {
  const { garage, access } = useGarage();
  if (!garage?.public_link_active || access?.blocked || access?.role !== "garage_manager") return null;
  return <ShareLinkInner garage={garage} />;
}

function ShareLinkInner({ garage }) {
  const key = `gf_share_chip_${garage.slug}`;
  const [chip, setChip] = useState(() => localStorage.getItem(key) === "1");
  const done = (now) => { localStorage.setItem(key, "1"); if (now) setChip(true); };
  const url = `${window.location.origin}/g/${garage.slug}`;
  const message = t("p7.share.message", { link: url });
  if (chip) return <button type="button" onClick={() => setChip(false)} className="inline-flex min-h-9 items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 text-xs font-semibold text-teal-800 transition-colors duration-150 hover:bg-teal-100" data-testid="share-link-chip"><Link2 className="h-3.5 w-3.5" />{t("p7.share.title")}</button>;
  return (
    <Card testId="share-link-card" className="relative">
      <button type="button" onClick={() => done(true)} aria-label={t("b13.dismiss")} className="absolute right-3 top-3 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid="share-link-dismiss"><X className="h-4 w-4" /></button>
      <SectionTitle hint={t("p7.share.hint")}><Link2 className="mr-2 inline h-4 w-4" />{t("p7.share.title")}</SectionTitle>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-lg [overflow-wrap:anywhere] bg-slate-100 px-3 py-2 text-sm text-slate-800" data-testid="share-link-url">{url}</code>
        <Button size="sm" onClick={() => { copy(url); done(false); }} data-testid="share-link-copy"><Copy className="mr-1.5 h-4 w-4" />{t("p7.share.copyLink")}</Button>
      </div>
      <div className="mt-4 flex flex-col items-stretch gap-2 sm:flex-row sm:items-start">
        <p className="min-w-0 flex-1 whitespace-pre-wrap rounded-lg border border-slate-200 p-3 text-sm text-slate-700 [overflow-wrap:anywhere]" data-testid="share-link-message">{message}</p>
        <Button size="sm" variant="outline" className="self-start" onClick={() => { copy(message); done(false); }} data-testid="share-message-copy"><Copy className="mr-1.5 h-4 w-4" />{t("p7.share.copyMessage")}</Button>
      </div>
    </Card>
  );
}
